/**
 * The one place in the product that renders outbound mail the way a vendor will
 * see it.
 *
 * Every preview of an outbound message goes through here — the signature
 * editor, the composer, the template editor — so the three of them cannot drift
 * into disagreeing about what the planner's own mail looks like.
 */

/**
 * The document the message is rendered into.
 *
 * Deliberately bare: the app's Tailwind is a design system for a planner-facing
 * SPA and has nothing to do with what Gmail will do to this markup. Rendering
 * against near-nothing — a default sans face at a mail-like size, on white — is
 * roughly what a mail client does, and stops the preview flattering markup that
 * will arrive looking different.
 *
 * The handful of element rules below are not styling, they are the defaults a
 * mail client brings: a paragraph has margins, a blockquote is indented with a
 * rule, an image does not overflow the column. Without them a body composed in
 * the rich editor previews as one run-on block, which is a different lie from
 * the one Tailwind would tell.
 */
function previewDocument(html: string, padding: number): string {
  return (
    '<!doctype html><html><head><meta charset="utf-8">' +
    '<style>html,body{margin:0;padding:0;background:#ffffff;color:#111111;}' +
    `body{padding:${padding}px;}` +
    'body{font-family:Helvetica Neue,Helvetica,Arial,sans-serif;font-size:14px;line-height:1.5;}' +
    'p{margin:0 0 1em;}p:last-child{margin-bottom:0;}' +
    'blockquote{margin:0.5em 0 0.5em 0.2em;padding-left:0.75em;' +
    'border-left:2px solid #dddddd;color:#555555;}' +
    'ul,ol{padding-left:1.5em;margin:0 0 1em;}' +
    'img{max-width:100%;height:auto;}</style></head><body>' +
    html +
    '</body></html>'
  );
}

/**
 * An inert frame containing message html.
 *
 * Why it is *this* inert, in two layers:
 *
 *  - `pointer-events: none` — a preview is not a control. Without it a planner
 *    can click a link in their own signature and navigate away from a
 *    half-written message, losing it; the textarea is local state with no
 *    draft behind it.
 *  - `sandbox="allow-same-origin"` — defence in depth for the route
 *    `pointer-events` does not cover (keyboard focus reaching a link). No
 *    `allow-scripts`, no `allow-popups`, no `allow-top-navigation`, so nothing
 *    inside the frame can act on the page around it. `allow-same-origin` stays
 *    because the height measurement below needs `contentDocument`, and because
 *    the `/api/signature/assets/…` and `/api/message-assets/…` image sources
 *    have to resolve.
 *
 * The height is measured from the loaded document rather than fixed: a
 * signature is a few lines and an inquiry is a dozen, and a fixed box gives one
 * of them a scrollbar and the other a lake of white space. `srcDoc` changing
 * remounts the document, so `onLoad` fires again and the frame re-measures.
 */
export function MailFrame({
  html,
  title,
  className,
  padding = 32,
}: {
  html: string;
  title: string;
  className?: string;
  /**
   * The gutter inside the frame, in px.
   *
   * 32 is what every preview has always had — `html` and `body` each carried
   * 16 of it — and it stays the default rather than quietly re-spacing five
   * screens. The composer's letter passes 16: there the frame is the foot of
   * a message whose body already has its own gutter, and two different
   * indents inside one letter read as a mistake.
   */
  padding?: number;
}) {
  return (
    <iframe
      title={title}
      sandbox="allow-same-origin"
      srcDoc={previewDocument(html, padding)}
      className={`pointer-events-none w-full rounded-2xl border border-border bg-white ${className ?? ''}`}
      onLoad={(e) => {
        const doc = e.currentTarget.contentDocument;
        // The larger of the two: `body.scrollHeight` misses a trailing margin
        // that escapes the body box — a signature block ending in a paragraph
        // is exactly that — and the few pixels it misses are enough to put a
        // scrollbar down the side of a preview nobody can scroll (the frame is
        // `pointer-events: none`). +4 for the border box, without which the
        // last line sits under that scrollbar on browsers that round
        // differently.
        if (doc) {
          const height = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
          e.currentTarget.style.height = `${height + 4}px`;
        }
      }}
    />
  );
}
